import withAuth from "@auth/withAuth";
import { useToast } from "@celebration/react";
import {
  Button,
  Flex,
  Icons,
  Input,
  Modal,
  Table,
  Tooltip,
} from "@components/index";
import Loading from "@fragments/Loading.fragment";
import {
  useGetDangerZone,
  useUpdateDangerZone,
} from "@services/dangerZone/hooks";
import { useGetGoalsConfig, useUpdateGoalsConfig } from "@services/goalsConfig/hooks";
import { useGetAllProcess } from "@services/process/hooks";
import { useEffect, useState } from "react";
import { Checkbox, Radio, RadioGroup } from "rsuite";
import styles from "./DangerZones.module.scss";

const DangerZones: React.FC = () => {
  const toast = useToast();

  const { data, isLoading } = useGetDangerZone();
  const updateMutation = useUpdateDangerZone();
  const updateMeta = useUpdateGoalsConfig();

  const { data: dataMeta } = useGetGoalsConfig();
  const { data: dataProcess } = useGetAllProcess();

  const [lowRiskTime, setLowRiskTime] = useState<number>();
  const [mediumRiskTime, setMediumRiskTime] = useState<number>();
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [tipeGoal, setTipeGoal] =
    useState<Sentinel.Enumerators.GoalConfigType>();
  const [processes, setProcesses] = useState<number[]>([]);

  useEffect(() => {
    if (data) {
      setLowRiskTime(data.lowRiskTime);
      setMediumRiskTime(data.mediumRiskTime);
    }
  }, [data]);

  useEffect(() => {
    if (dataMeta) {
      console.log(dataMeta.goalConfigType);
      setTipeGoal(dataMeta.goalConfigType);
      setProcesses(dataMeta.processDefinitionIds);
    }
  }, [dataMeta]);

  const isValid = lowRiskTime < mediumRiskTime && mediumRiskTime;

  const handleSaveMeta = async () => {
    try {
      await updateMeta.mutateAsync({
        goalConfigType: tipeGoal,
        processDefinitionIds: processes,
      });

      toast.openToast({
        title: "Sucesso",
        description: "Configuração de meta atualizada com sucesso!",
        type: "positive",
        autoClose: true,
      });
    } catch (error) {
      toast.openToast({
        title: "Erro",
        description: "Erro ao salvar configuração de meta",
        type: "negative",
        autoClose: true,
      });
    }
  };

  const handleSave = async () => {
    if (!isValid) {
      toast.openToast({
        title: "Validação",
        description:
          "Os tempos devem seguir a ordem: Risco Baixo < Risco Médio < Risco Alto",
        type: "warning",
        autoClose: true,
      });
      return;
    }

    try {
      await updateMutation.mutateAsync({
        lowRiskTime,
        mediumRiskTime,
        highRiskTime: mediumRiskTime,
      });

      toast.openToast({
        title: "Sucesso",
        description: "Configuração de risco atualizada com sucesso!",
        type: "positive",
        autoClose: true,
      });

      setShowConfirmModal(false);
    } catch (error) {
      toast.openToast({
        title: "Erro",
        description: "Erro ao salvar configuração de risco",
        type: "negative",
        autoClose: true,
      });
    }
  };

  const openConfirmModal = () => {
    setShowConfirmModal(true);
  };

  const getRiskColor = (riskLevel: "low" | "medium" | "high") => {
    switch (riskLevel) {
      case "low":
        return "#28a745";
      case "medium":
        return "#ffc107";
      case "high":
        return "#dc3545";
      default:
        return "#6c757d";
    }
  };

  if (isLoading) {
    return <Loading show />;
  }

  return (
    <Flex.Col gap="40px" className="p-40">
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>
            Configuração de Risco para Processos Críticos
          </h1>
          <p className={styles.subtitle}>
            Defina quando processos críticos indicam risco baixo, médio e alto
          </p>
        </div>

        <div className={styles.content}>
          <div className={styles.cardsRow}>
            <div
              className={styles.card}
              style={{ borderColor: `${getRiskColor("low")}30` }}
            >
              <div
                className={styles.cardHeader}
                style={{ backgroundColor: `${getRiskColor("low")}15` }}
              >
                <h3 style={{ color: getRiskColor("low") }}>Risco Baixo</h3>
              </div>
              <div
                className={styles.cardContent}
                style={{ backgroundColor: `${getRiskColor("low")}15` }}
              >
                <Input.Number
                  label={`até ${lowRiskTime ?? "X"} minutos`}
                  value={lowRiskTime}
                  onChange={(event) =>
                    setLowRiskTime(parseInt(event.target.value) || 0)
                  }
                  min={1}
                  max={1440}
                  size="lg"
                />
              </div>
            </div>

            <div
              className={styles.card}
              style={{ borderColor: `${getRiskColor("medium")}30` }}
            >
              <div
                className={styles.cardHeader}
                style={{ backgroundColor: `${getRiskColor("medium")}15` }}
              >
                <h3 style={{ color: getRiskColor("medium") }}>Risco Médio</h3>
              </div>
              <div
                className={styles.cardContent}
                style={{ backgroundColor: `${getRiskColor("medium")}15` }}
              >
                <Input.Number
                  label={`de ${lowRiskTime ?? "X"} a ${mediumRiskTime ?? "Y"} minutos`}
                  value={mediumRiskTime}
                  onChange={(event) =>
                    setMediumRiskTime(parseInt(event.target.value) || 0)
                  }
                  min={1}
                  max={1440}
                  size="lg"
                />
              </div>
            </div>

            <div
              className={styles.card}
              style={{ borderColor: `${getRiskColor("high")}30` }}
            >
              <div
                className={styles.cardHeader}
                style={{ backgroundColor: `${getRiskColor("high")}15` }}
              >
                <h3 style={{ color: getRiskColor("high") }}>Risco Alto</h3>
              </div>
              <div
                className={styles.cardContent}
                style={{ backgroundColor: `${getRiskColor("high")}15` }}
              >
                <Input.Text
                  label={`acima de ${mediumRiskTime ?? "Y"} minutos`}
                  value="Automático"
                  disabled
                  size="lg"
                />
              </div>
            </div>
          </div>

          <div className={styles.actions}>
            <Button
              label="Salvar Configuração"
              size="lg"
              onClick={openConfirmModal}
              disabled={!isValid}
            />
            {!isValid && (
              <p className={styles.errorText}>
                Os tempos devem seguir a ordem: Risco Baixo &lt; Risco Médio
                &lt; Risco Alto
              </p>
            )}
          </div>
        </div>
      </div>

      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>Configuração de Meta</h1>
        </div>

        <Flex.Col gap="20px" className="pb-20">
          <Flex.Col gap="10px">
            <Flex.Row gap="5px">
              <p className={styles.subtitle}>Tipo de Meta</p>
              <Tooltip
                position="top"
                description="Binária: Todos os processos devem estar OK Percentual: Baseado em % de entregas"
              >
                <Icons icon="info" />
              </Tooltip>
            </Flex.Row>

            <RadioGroup
              name="radio-group-inline"
              inline
              value={tipeGoal}
              onChange={(event: Sentinel.Enumerators.GoalConfigType) => {
                setTipeGoal(event);
              }}
            >
              <Radio value="BINARY" color="orange">
                Binária
              </Radio>
              <Radio value="PERCENTAGE" color="orange">
                Percentual
              </Radio>
            </RadioGroup>
          </Flex.Col>

          <hr />

          <Flex.Row>
            <Table
              data={dataProcess}
              columns={[
                {
                  title: "Processos considerados para a meta",
                  cellRender: (
                    element: Sentinel.Processes.GetAllProcessesResponse
                  ) => (
                    <Flex.Row alignItems="center">
                      <Checkbox
                        color="orange"
                        checked={processes.includes(element.id)}
                        onChange={(_, checked) =>
                          setProcesses((prev) =>
                            checked
                              ? [...prev, element.id]
                              : prev.filter((id) => id !== element.id)
                          )
                        }
                      />
                      <span>{element.displayName}</span>
                    </Flex.Row>
                  ),
                },
                {
                  cellRender: (
                    element: Sentinel.Processes.GetAllProcessesResponse
                  ) => (
                    <Flex.Row justifyContent="end">
                      {element.isOneShot && (
                        <div className={styles.oneShot}>ONE SHOT</div>
                      )}
                      {element.critical && (
                        <div className={styles.critical}>CRÍTICO</div>
                      )}
                    </Flex.Row>
                  ),
                },
              ]}
            />
          </Flex.Row>
        </Flex.Col>

        <div className={styles.actions}>
          <Button label="Salvar Meta" size="lg" onClick={handleSaveMeta} />
        </div>
      </div>

      <Modal.Confirmation
        title="Confirmar Alterações"
        description="Você deseja realmente salvar as alterações na configuração de risco?"
        onCancel={() => setShowConfirmModal(false)}
        onConfirm={handleSave}
        open={showConfirmModal}
      />
    </Flex.Col>
  );
};

export default withAuth(DangerZones);
